import { api } from "@/api"
import { BASE_URL, ENDPOINTS } from "@/constants"
import { toast } from "@/hooks/use-toast"
import i18n from "@/i18n"
import { getApiErrorMessage } from "@/lib/api-error"
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@/libs"
import type {
  AppCredential,
  AppCredentialPage,
  AppCredentialSecret,
  CreateAppCredentialPayload,
  UpdateAppCredentialPayload,
} from "@/types/app-credential"

/** Chave base das queries de credencial — usada também na invalidação. */
export const APP_CREDENTIALS_QUERY_KEY =
  ENDPOINTS.APP_CREDENTIALS

/** Credenciais de um white label. Sem secret: ele tem rota própria. */
export const getAppCredentials = async (
  whiteLabelId: string
): Promise<AppCredentialPage> => {
  const url = `${BASE_URL}${ENDPOINTS.APP_CREDENTIALS}?whiteLabelId=${encodeURIComponent(whiteLabelId)}`
  const { data } = await api.get<AppCredentialPage>(url)
  return data
}

export const useAppCredentials = (whiteLabelId?: string) =>
  useQuery({
    queryKey: [APP_CREDENTIALS_QUERY_KEY, whiteLabelId],
    queryFn: () =>
      getAppCredentials(whiteLabelId as string),
    enabled: Boolean(whiteLabelId),
    gcTime: 0,
  })

export const createAppCredential = async (
  payload: CreateAppCredentialPayload
): Promise<AppCredential> => {
  const url = `${BASE_URL}${ENDPOINTS.APP_CREDENTIALS}`
  const { data } = await api.post<AppCredential>(
    url,
    payload
  )
  return data
}

export const useCreateAppCredentialMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<
    AppCredential,
    unknown,
    CreateAppCredentialPayload
  >({
    mutationFn: createAppCredential,
    onSuccess: async () => {
      toast({
        title: i18n.t(
          "pages.adminWhiteLabels.appCredentials.createSuccess"
        ),
        variant: "success",
      })
      await queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === APP_CREDENTIALS_QUERY_KEY,
      })
    },
    onError: (error: unknown) => {
      toast({
        title: getApiErrorMessage(
          error,
          i18n.t(
            "pages.adminWhiteLabels.appCredentials.createError"
          )
        ),
        variant: "destructive",
      })
    },
  })
}

export const updateAppCredential = async ({
  uuid,
  ...payload
}: UpdateAppCredentialPayload): Promise<AppCredential> => {
  const url = `${BASE_URL}${ENDPOINTS.APP_CREDENTIALS}/${uuid}`
  const { data } = await api.put<AppCredential>(
    url,
    payload
  )
  return data
}

export const useUpdateAppCredentialMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<
    AppCredential,
    unknown,
    UpdateAppCredentialPayload
  >({
    mutationFn: updateAppCredential,
    onSuccess: async () => {
      toast({
        title: i18n.t(
          "pages.adminWhiteLabels.appCredentials.updateSuccess"
        ),
        variant: "success",
      })
      await queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === APP_CREDENTIALS_QUERY_KEY,
      })
    },
    onError: (error: unknown) => {
      toast({
        title: getApiErrorMessage(
          error,
          i18n.t(
            "pages.adminWhiteLabels.appCredentials.updateError"
          )
        ),
        variant: "destructive",
      })
    },
  })
}

/**
 * Remoção da credencial: revoga o acesso do parceiro na hora. No gateway é um soft delete
 * (`deleted_at`) que edge e partner já respeitam, então a linha fica para auditoria mas não
 * autentica mais — e o portal não oferece desfazer. A tela confirma antes de chamar.
 */
export const deleteAppCredential = async (
  uuid: string
): Promise<void> => {
  const url = `${BASE_URL}${ENDPOINTS.APP_CREDENTIALS}/${uuid}`
  await api.delete(url)
}

export const useDeleteAppCredentialMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<void, unknown, string>({
    mutationFn: deleteAppCredential,
    onSuccess: async () => {
      toast({
        title: i18n.t(
          "pages.adminWhiteLabels.appCredentials.deleteSuccess"
        ),
        variant: "success",
      })
      await queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === APP_CREDENTIALS_QUERY_KEY,
      })
    },
    onError: (error: unknown) => {
      toast({
        title: getApiErrorMessage(
          error,
          i18n.t(
            "pages.adminWhiteLabels.appCredentials.deleteError"
          )
        ),
        variant: "destructive",
      })
    },
  })
}

/**
 * Leitura do clientSecret — chamada sob demanda, nunca no carregamento da lista.
 *
 * É uma mutation e não uma query de propósito: revelar segredo é um ATO do operador (auditado no
 * gateway), não um dado que a tela mantém em cache e revalida sozinha.
 */
export const revealAppCredentialSecret = async (
  uuid: string
): Promise<AppCredentialSecret> => {
  const url = `${BASE_URL}${ENDPOINTS.APP_CREDENTIALS}/${uuid}/secret`
  const { data } = await api.get<AppCredentialSecret>(url)
  return data
}

export const useRevealAppCredentialSecretMutation = () =>
  useMutation<AppCredentialSecret, unknown, string>({
    mutationFn: revealAppCredentialSecret,
    onError: (error: unknown) => {
      toast({
        title: getApiErrorMessage(
          error,
          i18n.t(
            "pages.adminWhiteLabels.appCredentials.secretError"
          )
        ),
        variant: "destructive",
      })
    },
  })
